import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { OrderStatus } from '@/components/chat/utils/order-status';
import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import OrderTable from '@/components/table/order/OrderTable';
import Button from '@/components/ui/button';
import { DangerIcon } from '@/components/ui/svg/DashboardIcons';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import { formatDate, formatPrice } from '@/components/utils/utils';
import { useUser } from '@/hooks/useUser';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { ICollectionOrder } from '@/interfaces/api/orders/ICollectionOrder';
import { IOrderWithAction } from '@/interfaces/api/orders/IOrderWithAction';
import { UserRole } from '@/interfaces/api/user/UserRole.enum';
import { ordersApiService } from '@/services/order.service';

const OrderDashboard = () => {
	const { user } = useUser();
	const userIsConsumer = user?.roles.includes(UserRole.Consumer);

	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [myOrdersCheck, setMyOrderCheck] = useState(
		userIsConsumer ? true : false,
	);
	const [pageNumber, setPageNumber] = useState(1);
	const [filterSelected, setFilterSelected] = useState<OrderStatus | undefined>(
		undefined,
	);
	const [searchQuery, setSearchQuery] = useState('');

	const handlePaginationOptions = (
		collection: ICollectionOrder,
	): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: collection.pageCount,
			itemCount: collection.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	const selectedFilters =
		filterSelected || searchQuery
			? {
					status: filterSelected,
					id: searchQuery ? Number(searchQuery) : undefined,
			  }
			: undefined;

	const columnHelper = createColumnHelper<IOrderWithAction>();
	const {
		data: orders,
		refetch,
		isLoading,
		error,
		isError,
	} = useQuery<ICollectionOrder>({
		queryKey: [
			myOrdersCheck ? ApiQueryKeys.getMyOrders : ApiQueryKeys.getOrders,
			pageSize,
			pageNumber,
			myOrdersCheck,
		],
		queryFn: () =>
			ordersApiService.getOrders(
				{ number: pageNumber, size: pageSize },
				myOrdersCheck,
				selectedFilters,
			),
		refetchOnWindowFocus: false,
	});

	const { isLoading: isExportingCSV, refetch: exportCSV } = useQuery<Blob>({
		queryKey: ['exportOrdersCSV', myOrdersCheck],
		queryFn: async () => {
			try {
				const blob = await ordersApiService.exportOrdersAsCSV(myOrdersCheck);
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				const filename = `orders-${myOrdersCheck ? 'my' : 'sell'}-${
					new Date().toISOString().split('T')[0]
				}.csv`;
				link.setAttribute('download', filename);
				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(url);
				toast.success('Orders exported successfully!');
				return blob;
			} catch (error) {
				console.error('Failed to export CSV:', error);
				toast.error('Failed to export orders. Please try again.');
				throw error;
			}
		},
		enabled: false,
		refetchOnWindowFocus: false,
		retry: false,
	});

	const columns = [
		columnHelper.accessor('id', {
			header: () => <div className="w-[50px] text-center px-2"> ID</div>,
			cell: (info) => (
				<div className="w-[50px] text-center px-2 whitespace-normal">
					{info.getValue()}
				</div>
			),
		}),
		columnHelper.accessor('createdAt', {
			header: () => <div className="w-[150px] text-center px-2">Date</div>,
			cell: (info) => (
				<div className="w-[150px] text-center px-2 whitespace-normal">
					{formatDate(info.getValue())}
				</div>
			),
		}),
		columnHelper.accessor('customerEmail', {
			header: () => (
				<div className="w-[250px] text-center px-2">Customer Email</div>
			),
			cell: (info) => (
				<div className="w-[250px] text-center px-2 whitespace-normal break-words">
					{info.getValue()}
				</div>
			),
		}),
		columnHelper.accessor('customerName', {
			header: () => (
				<div className="w-[200px] text-center px-2">Customer Name</div>
			),
			cell: (info) => (
				<div className="w-[200px] text-center px-2 whitespace-normal">
					{info.getValue()}
				</div>
			),
		}),
		columnHelper.accessor('status', {
			header: () => <div className="w-[100px] text-center px-2 ">Status</div>,
			cell: (info) => (
				<div
					className={`w-[100px] text-center px-2 whitespace-normal capitalize rounded-md
						${
							info.getValue() === OrderStatus.PENDING
								? 'bg-yellow-100 text-yellow-800'
								: ''
						}
						${
							info.getValue() === OrderStatus.DELIVERED
								? 'bg-purpleBrand-100 text-purpleBrand-800'
								: ''
						}
						${info.getValue() === OrderStatus.CANCELLED ? 'bg-red-100 text-red-800' : ''}
						${info.getValue() === OrderStatus.PROCESSING ? 'bg-blue-100 text-blue-800' : ''}
						${info.getValue() === OrderStatus.SHIPPED ? 'bg-gray-200 text-gray-800' : ''}
					`}
				>
					{info.getValue()}
				</div>
			),
		}),

		columnHelper.accessor('agreement', {
			header: () => <div className="w-[80px] text-center px-2">Price</div>,
			cell: (info) => (
				<div className="w-[80px] text-center px-2">
					{info?.getValue() ? formatPrice(info?.getValue()?.final_price) : ''}
				</div>
			),
		}),
		columnHelper.accessor('agreement', {
			header: () => <div className="w-[80px] text-center px-2">Units</div>,
			cell: (info) => (
				<div className="w-[80px] text-center px-2">
					{info?.getValue()?.units}
				</div>
			),
		}),
		columnHelper.accessor('agreement', {
			header: () => <div className="w-[80px] text-center px-2">Total Bill</div>,
			cell: (info) => (
				<div className="w-[80px] text-center px-2">
					{info?.getValue()
						? formatPrice(
								info?.getValue()?.units * info?.getValue()?.final_price,
						  )
						: ''}
				</div>
			),
		}),

		columnHelper.accessor('shippingAddress', {
			header: () => (
				<div className="w-[200px] text-center px-2">Shipping Address</div>
			),
			cell: (info) => (
				<div className="w-[200px] text-center px-2">{info.getValue()}</div>
			),
		}),
		columnHelper.accessor('vendorEmail', {
			header: () => (
				<div className="w-[250px] text-center px-2">Vendor Email</div>
			),
			cell: (info) => (
				<div className="w-[250px] text-center px-2 whitespace-normal break-words">
					{info.getValue()}
				</div>
			),
		}),
	];

	useEffect(() => {
		refetch();
	}, [filterSelected, refetch]);

	return (
		<div className="bg-grey-5 min-h-content overflow-y-auto">
			<div className="flex gap-3 flex-wrap m-5 justify-between items-center">
				<div className="flex gap-3 flex-wrap">
					{!userIsConsumer && (
						<Button
							color="white"
							className="border !border-grey-30"
							shape="rounded"
							transition="none"
							onClick={() => setMyOrderCheck(false)}
							data-test="update-data-modal-button"
							disabled={!myOrdersCheck}
						>
							Sell Orders
						</Button>
					)}
					<Button
						color="white"
						className="border !border-grey-30"
						shape="rounded"
						transition="none"
						onClick={() => setMyOrderCheck(true)}
						data-test="update-password-modal-button"
						disabled={myOrdersCheck}
					>
						My Orders
					</Button>
				</div>
				<Button
					color="primary"
					className="bg-blueBrand-600 hover:bg-blueBrand-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
					shape="rounded"
					transition="none"
					onClick={() => exportCSV()}
					disabled={isExportingCSV}
					data-test="export-csv-button"
				>
					{isExportingCSV ? 'Exporting...' : 'Export CSV'}
				</Button>
			</div>
			{isLoading && <LoadingSkeletonModalTable />}
			{isError && (
				<div className="flex flex-col items-center justify-center h-[400px] bg-grey-0 rounded-large shadow">
					<div className="text-redBrand-600 mb-4">
						<DangerIcon />
					</div>
					<h3 className="text-lg font-semibold text-grey-90 mb-2">
						Failed to load orders
					</h3>
					<p className="text-grey-50 mb-4">
						{error instanceof Error
							? error.message
							: 'An unexpected error occurred'}
					</p>
					<button
						onClick={() => {
							setSearchQuery('');
							refetch();
						}}
						className="px-4 py-2 bg-blueBrand-600 text-white rounded-md hover:bg-blueBrand-700 transition-colors"
					>
						Try Again
					</button>
				</div>
			)}

			{!isError && orders && !isLoading && (
				<div className="flex flex-col h-full grow gap-4">
					<OrderTable
						collection={orders}
						paginationOption={handlePaginationOptions(orders)}
						columns={columns}
						refetch={refetch}
						query={searchQuery}
						setQuery={setSearchQuery}
						setFilterSelected={(val: string) => {
							val === ''
								? setFilterSelected(undefined)
								: setFilterSelected(val as OrderStatus);
						}}
					/>
				</div>
			)}
		</div>
	);
};
export default OrderDashboard;
